import { useEffect, useRef } from 'react'
import { leaveGame, markPresent, signalAway } from '@/shared/game/api'
import { isLocalGame } from '@/shared/local/store'

/** Как часто страница стола отмечается. Сервер ждёт с запасом (config/ludo.php, lobby). */
const EVERY_MS = 15_000

/**
 * Выход, отложенный до конца текущего такта, — по адресу партии.
 *
 * В режиме разработки React размонтирует и тут же монтирует компонент
 * заново (StrictMode): без отсрочки игрок уходил бы из-за стола на первом
 * же показе. Вернувшийся компонент отменяет выход.
 */
const pending = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * Присутствие за незапущенным столом (docs/RULES.md, «Уход из партии»).
 *
 * Ушедший со страницы стола — любым способом — снимается с места:
 *
 * - переход внутри игры (на главную, назад) — выход сразу, как по кнопке;
 * - закрыл вкладку, ушёл на другой сайт, обновляет страницу — сигнал
 *   «закрываюсь», и через несколько секунд сервер снимает с места, если
 *   страница не вернулась (обновлённая возвращается и отмечается);
 * - пропала связь, села батарея — страница перестаёт отмечаться, и сервер
 *   снимает с места через полторы минуты.
 *
 * Только лобби: начатую партию за ушедшего доигрывает компьютер, и
 * выкидывать из неё за закрытую вкладку нельзя.
 */
export function useLobbyPresence(gameId: string, inLobby: boolean, seated: boolean): void {
  const active = inLobby && seated && !isLocalGame(gameId)
  const activeRef = useRef(active)

  activeRef.current = active

  useEffect(() => {
    if (!active) return

    const ping = () => {
      // Отказ — не беда: снятого с места экран увидит по состоянию стола.
      markPresent(gameId).catch(() => undefined)
    }
    const hide = () => signalAway(gameId)
    // Страница, вернувшаяся из кеша браузера («назад» на другом сайте), —
    // та же самая, и таймеры у неё стояли: отметиться надо сразу.
    const show = (event: PageTransitionEvent) => {
      if (event.persisted) ping()
    }

    ping()
    const timer = setInterval(ping, EVERY_MS)

    window.addEventListener('pagehide', hide)
    window.addEventListener('pageshow', show)

    return () => {
      clearInterval(timer)
      window.removeEventListener('pagehide', hide)
      window.removeEventListener('pageshow', show)
    }
  }, [gameId, active])

  // Уход со страницы стола внутри игры — выход сразу. Отдельным эффектом
  // только от адреса: смена «в лобби → партия началась» уходом не является.
  useEffect(() => {
    const again = pending.get(gameId)

    if (again !== undefined) {
      clearTimeout(again)
      pending.delete(gameId)
    }

    return () => {
      if (!activeRef.current) return

      pending.set(
        gameId,
        setTimeout(() => {
          pending.delete(gameId)
          leaveGame(gameId).catch(() => undefined)
        }, 0),
      )
    }
  }, [gameId])
}
